import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Divider, Message, Modal, useToaster } from "rsuite";
import * as Yup from "yup";
import { authCRUD } from "../../../actions/auth";
import { Input, Select } from "antd";
import { selector } from "../../../api";
import { char, code } from "../../../utils";

const studyYearSchema = Yup.object().shape({
  email: Yup.string()
    .email("This is not a valid email format")
    .required("This field is required"),

  first_name: Yup.string().required("This field is required"),

  gender: Yup.string().required("This field is required"),

  phone_number: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must be digits only")
    .min(
      12,
      "Phone number should be 12 digits or more and must start with phone code eg 255"
    )
    .required("This field is required"),

  password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .matches(/\d/, "Password must contain at least one digit")
    .test(
      "not-first-name",
      "Password should not be the same as your first name",
      function (value) {
        const { first_name } = this.parent;
        return value && first_name
          ? value.toLowerCase() !== first_name.toLowerCase()
          : true;
      }
    )
    .required("Password is required"),

  password2: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
});

const initialValues = {
  email: "",
  first_name: "",
  last_name: "",
  gender: "",
  phone_number: "",
  is_active: true,
  password: "",
  password2: ""
};

export const UserAdd = (props) => {
  const { open, setOpen, index, setIndex } = props;
  const [loading, setLoading] = useState(false);
  const toaster = useToaster();

  const user = selector.User();



  

  const formik = useFormik({
    initialValues,
    validationSchema: studyYearSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      setLoading(true);
      const {
        first_name,
        last_name,
        email,
        phone_number,
        gender,
        password,
        is_active
      } = values;
      const data = {
        first_name,
        last_name,
        email,
        phone_number,
        gender,
        password,
        username: email,
        is_active,
        recorded_by: user.id,
      };

      authCRUD
        .addUser(data)
        .then((resp) => {
          toaster.push(
            <Message type="success" showIcon header={"Success"} closable>
              <p className="h6">
                <p className="fw-bold fs-14">Thank you...!</p>
                <p className="text-dark fs-12">
                  User has been added successfully.
                </p>
              </p>
            </Message>,
            { placement: "topEnd", duration: 7000 }
          );
          setIndex(index + 1);
          resetForm();
          setLoading(false);
          setSubmitting(false);
          setOpen(false);
        })
        .catch(() => {
          setLoading(false);
          setSubmitting(false);
        });
    },
  });

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)} size="md">
        <Modal.Header>
          <Modal.Title>New User</Modal.Title>
        </Modal.Header>
        <Divider />
        <form onSubmit={formik.handleSubmit}>
          <Modal.Body>
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label className="mb-1">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <Input
                      className="form-control"
                      placeholder="First Name"
                      {...formik.getFieldProps("first_name")}
                      status={
                        formik.touched.first_name &&
                        formik.errors.first_name &&
                        "error"
                      }
                    />
                    {formik.touched.first_name && formik.errors.first_name && (
                      <div className="fw-light mt-1 fs-15">
                        <span role="alert" className="text-danger">
                          {formik.errors.first_name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label className="mb-1">
                      Gender <span className="text-danger">*</span>
                    </label>
                    <Select
                      size="large"
                      className="w-100"
                      showSearch
                      placeholder="Select gender"
                      optionFilterProp="label"
                      filterOption={filterOption}
                      onChange={(e) => formik.setFieldValue("gender", e)}
                      options={[
                        {
                          value: "male",
                          label: "Male",
                        },
                        {
                          value: "female",
                          label: "Female",
                        },
                        {
                          value: "other",
                          label: "Others",
                        },
                      ]}
                      status={
                        formik.touched.gender && formik.errors.gender && "error"
                      }
                    />
                    {formik.touched.gender && formik.errors.gender && (
                      <div className="fw-light mt-1 fs-15">
                        <span role="alert" className="text-danger">
                          {formik.errors.gender}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label className="mb-1">
                      Email <span className="text-danger">*</span>
                    </label>
                    <Input
                      className="form-control"
                      placeholder="Email Address"
                      {...formik.getFieldProps("email")}
                      status={
                        formik.touched.email && formik.errors.email && "error"
                      }
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="fw-light mt-1 fs-15">
                        <span role="alert" className="text-danger">
                          {formik.errors.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label className="mb-1">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="Phone Number"
                      {...formik.getFieldProps("phone_number")}
                      status={
                        formik.touched.phone_number &&
                        formik.errors.phone_number &&
                        "error"
                      }
                    />
                    {formik.touched.phone_number &&
                      formik.errors.phone_number && (
                        <div className="fw-light mt-1 fs-15">
                          <span role="alert" className="text-danger">
                            {formik.errors.phone_number}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label className="mb-1">
                      Password <span className="text-danger">*</span>
                    </label>
                    <Input.Password
                      type="text"
                      size="large"
                      placeholder="Enter Password"
                      {...formik.getFieldProps("password")}
                      status={
                        formik.touched.password &&
                        formik.errors.password &&
                        "error"
                      }
                    />
                    {formik.touched.password && formik.errors.password && (
                      <div className="fw-light mt-1 fs-15">
                        <span role="alert" className="text-danger">
                          {formik.errors.password}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label className="mb-1">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <Input.Password
                      type="text"
                      size="large"
                      placeholder="Enter Confirm Password"
                      {...formik.getFieldProps("password2")}
                      status={
                        formik.touched.password2 &&
                        formik.errors.password2 &&
                        "error"
                      }
                    />
                    {formik.touched.password2 && formik.errors.password2 && (
                      <div className="fw-light mt-1 fs-15">
                        <span role="alert" className="text-danger">
                          {formik.errors.password2}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Divider />
          <Modal.Footer>
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-6 col-sm-12 d-flex justify-content-start">
                  <span className="text-danger">*</span> &nbsp; Required field
                </div>
                <div className="col-md-6 col-sm-12 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-danger me-3"
                    onClick={() => formik.resetForm()}
                  >
                    <span className="bi bi-slash-circle"></span> Clear
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={formik.isSubmitting}
                  >
                    {!loading ? (
                      <span className="indicator-label">
                        <span className="bi bi-check-circle"></span> Save
                      </span>
                    ) : (
                      <span
                        className="indicator-progress"
                        style={{ display: "block" }}
                      >
                        Please wait.....
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
